// features/permission/permissionSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate } from '../hooks/axiosInstance';

// Get all permissions
export const getAllPermissions = createAsyncThunk(
  'permission/getAllPermissions',
  async (params = {}, thunkAPI) => {
    try {
      const { page = 1, limit = 10, level, search } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(level && { level: level.toString() }),
        ...(search && { search })
      });

      const response = await axiosPrivate.get(`/permissions?${queryParams}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Get user permission
export const getUserPermission = createAsyncThunk(
  'permission/getUserPermission',
  async (userId, thunkAPI) => {
    try {
      const response = await axiosPrivate.get(`/permissions/user/${userId}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Set user permission
export const setUserPermission = createAsyncThunk(
  'permission/setUserPermission',
  async ({ userId, permissionData }, thunkAPI) => {
    try {
      const response = await axiosPrivate.post(`/permissions/user/${userId}`, permissionData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Delete user permission
export const deleteUserPermission = createAsyncThunk(
  'permission/deleteUserPermission',
  async ({ userId, reason }, thunkAPI) => {
    try {
      const response = await axiosPrivate.delete(`/permissions/user/${userId}`, {
        data: { reason }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Get permission statistics
export const getPermissionStats = createAsyncThunk(
  'permission/getPermissionStats',
  async (_, thunkAPI) => {
    try {
      const response = await axiosPrivate.get('/permissions/stats');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Get unassigned users
export const getUnassignedUsers = createAsyncThunk(
  'permission/getUnassignedUsers',
  async (params = {}, thunkAPI) => {
    try {
      const { page = 1, limit = 10 } = params;
      const response = await axiosPrivate.get(`/permissions/unassigned?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

const permissionSlice = createSlice({
  name: 'permission',
  initialState: {
    // Permissions list
    permissions: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalPermissions: 0,
      limit: 10
    },
    
    // Current user permission (for editing)
    currentUserPermission: null,
    
    // Unassigned users
    unassignedUsers: [],
    unassignedPagination: {
      currentPage: 1,
      totalPages: 1,
      totalUsers: 0,
      limit: 10
    },
    
    // Statistics
    stats: {
      totalUsers: 0,
      totalPermissions: 0,
      usersWithoutPermissions: 0,
      levelDistribution: {},
      recentActivity: []
    },
    
    // Loading states
    loading: false,
    statsLoading: false,
    userPermissionLoading: false,
    unassignedLoading: false,
    
    // UI states
    error: null,
    success: null,
    
    // Filter states
    filters: {
      level: null,
      search: ''
    },
    
    // Modal states
    editModalVisible: false,
    deleteModalVisible: false,
    selectedUserId: null,
    
    // Form data
    formData: {
      level: 1,
      notes: '',
      reason: ''
    }
  },
  reducers: {
    // Clear messages
    clearMessages: (state) => {
      state.error = null;
      state.success = null;
    },
    
    // Update filters
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.filters = {
        level: null,
        search: ''
      };
    },
    
    // Modal controls
    openEditModal: (state, action) => {
      state.editModalVisible = true;
      state.selectedUserId = action.payload.userId;
      if (action.payload.permission) {
        state.formData = {
          level: action.payload.permission.level,
          notes: action.payload.permission.notes || '',
          reason: ''
        };
      }
    },
    
    closeEditModal: (state) => {
      state.editModalVisible = false;
      state.selectedUserId = null;
      state.formData = {
        level: 1,
        notes: '',
        reason: ''
      };
      state.currentUserPermission = null;
    },
    
    openDeleteModal: (state, action) => {
      state.deleteModalVisible = true;
      state.selectedUserId = action.payload.userId;
    },
    
    closeDeleteModal: (state) => {
      state.deleteModalVisible = false;
      state.selectedUserId = null;
      state.formData = {
        ...state.formData,
        reason: ''
      };
    },
    
    // Update form data
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    
    // Reset form
    resetForm: (state) => {
      state.formData = {
        level: 1,
        notes: '',
        reason: ''
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Get All Permissions
      .addCase(getAllPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = action.payload.permissions;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to fetch permissions';
      })

      // Get User Permission
      .addCase(getUserPermission.pending, (state) => {
        state.userPermissionLoading = true;
        state.error = null;
      })
      .addCase(getUserPermission.fulfilled, (state, action) => {
        state.userPermissionLoading = false;
        state.currentUserPermission = action.payload;
        // Update form data with current permission
        if (action.payload.level) {
          state.formData = {
            ...state.formData,
            level: action.payload.level,
            notes: action.payload.notes || ''
          };
        }
      })
      .addCase(getUserPermission.rejected, (state, action) => {
        state.userPermissionLoading = false;
        state.error = action.payload?.msg || 'Failed to fetch user permission';
      })

      // Set User Permission
      .addCase(setUserPermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setUserPermission.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.msg;
        state.currentUserPermission = action.payload.permission;
        
        // Update permission in the list
        const index = state.permissions.findIndex(p => 
          p.userId._id === action.payload.permission.userId._id
        );
        if (index !== -1) {
          state.permissions[index] = action.payload.permission;
        } else {
          state.permissions.unshift(action.payload.permission);
        }
        
        // Close modal
        state.editModalVisible = false;
        state.selectedUserId = null;
      })
      .addCase(setUserPermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to set user permission';
      })

      // Delete User Permission
      .addCase(deleteUserPermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserPermission.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.msg;
        
        // Update permission in the list
        const index = state.permissions.findIndex(p => 
          p.userId._id === action.payload.permission.userId._id
        );
        if (index !== -1) {
          state.permissions[index] = action.payload.permission;
        }
        
        // Close modal
        state.deleteModalVisible = false;
        state.selectedUserId = null;
      })
      .addCase(deleteUserPermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to delete user permission';
      })

      // Get Permission Stats
      .addCase(getPermissionStats.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(getPermissionStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(getPermissionStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload?.msg || 'Failed to fetch permission stats';
      })

      // Get Unassigned Users
      .addCase(getUnassignedUsers.pending, (state) => {
        state.unassignedLoading = true;
        state.error = null;
      })
      .addCase(getUnassignedUsers.fulfilled, (state, action) => {
        state.unassignedLoading = false;
        state.unassignedUsers = action.payload.users;
        state.unassignedPagination = action.payload.pagination;
      })
      .addCase(getUnassignedUsers.rejected, (state, action) => {
        state.unassignedLoading = false;
        state.error = action.payload?.msg || 'Failed to fetch unassigned users';
      });
  }
});

export const {
  clearMessages,
  updateFilters,
  clearFilters,
  openEditModal,
  closeEditModal,
  openDeleteModal,
  closeDeleteModal,
  updateFormData,
  resetForm
} = permissionSlice.actions;

export default permissionSlice.reducer;